import { format } from "date-fns";
import upperFirst from "lodash/upperFirst";

import { formatSlotTimeRange } from "utils/dedicated-slots.util";

/**
 * SN-19902 (behind AMBIENT_SCRIBE_RECORDING_RESILIENCE): a stretch of a live recording that never
 * reached the transcript, so it will not be in the note.
 *
 * - `audio_stopped`: no audio arrived from the microphone, usually because the computer slept.
 * - `connection`: the socket to Suno was down. The audio was still captured to the local backup,
 *   but nothing transcribes that backup, which is why the copy says "missing from the
 *   transcript" rather than "not recorded".
 */
export type RecordingInterruptionCause = "audio_stopped" | "connection";

export interface RecordingInterruption {
  fromMs: number;
  /** null while the recording is still paused by it. */
  toMs: number | null;
  cause: RecordingInterruptionCause;
}

/** The shortest gap worth telling the provider about (SN-19902: "any gap of 30 s or more"). */
export const RECORDING_GAP_NOTICE_THRESHOLD_MS = 30_000;

/** A sleep usually drops the audio and the socket a few seconds apart: one event, not two. */
export const INTERRUPTION_MERGE_SLACK_MS = 5_000;

const endOf = (interruption: RecordingInterruption) =>
  interruption.toMs ?? Number.POSITIVE_INFINITY;

const overlaps = (a: RecordingInterruption, b: RecordingInterruption) =>
  a.fromMs <= endOf(b) + INTERRUPTION_MERGE_SLACK_MS &&
  b.fromMs <= endOf(a) + INTERRUPTION_MERGE_SLACK_MS;

const combine = (
  a: RecordingInterruption,
  b: RecordingInterruption
): RecordingInterruption => ({
  fromMs: Math.min(a.fromMs, b.fromMs),
  toMs: a.toMs === null || b.toMs === null ? null : Math.max(a.toMs, b.toMs),
  // "Your computer may have gone to sleep" is the explanation the provider can act on.
  cause:
    a.cause === "audio_stopped" || b.cause === "audio_stopped"
      ? "audio_stopped"
      : "connection"
});

/** Adds `next`, folding in every gap it overlaps (directly or through another), oldest first. */
export const mergeInterruptions = (
  list: RecordingInterruption[],
  next: RecordingInterruption
): RecordingInterruption[] => {
  let merged = next;
  let rest = list;
  let absorbedAny = true;
  while (absorbedAny) {
    absorbedAny = false;
    const kept: RecordingInterruption[] = [];
    for (const existing of rest) {
      if (overlaps(existing, merged)) {
        merged = combine(existing, merged);
        absorbedAny = true;
      } else {
        kept.push(existing);
      }
    }
    rest = kept;
  }
  return [...rest, merged].sort((a, b) => a.fromMs - b.fromMs);
};

/** "about 40 seconds", "about 1 minute", "about 18 minutes". */
export const formatGapDuration = (durationMs: number): string => {
  if (durationMs < 55_000) {
    const seconds = Math.max(10, Math.round(durationMs / 10_000) * 10);
    return `about ${seconds} seconds`;
  }
  const minutes = Math.max(1, Math.round(durationMs / 60_000));
  return `about ${minutes} ${minutes === 1 ? "minute" : "minutes"}`;
};

const formatClockTime = (ms: number) => format(new Date(ms), "h:mm a");

/** "5:07–5:25 PM", or "at 5:07 PM" when both ends fall in the same minute. */
export const formatGapRange = (fromMs: number, toMs: number): string => {
  if (formatClockTime(fromMs) === formatClockTime(toMs)) {
    return `at ${formatClockTime(fromMs)}`;
  }
  return formatSlotTimeRange(new Date(fromMs), new Date(toMs));
};

export const INTERRUPTION_CARD_TITLE =
  "Part of this visit is missing from the transcript, so it won't be in the note.";

const CAUSE_SENTENCE: Record<RecordingInterruptionCause, string> = {
  connection: "The connection to Suno was lost.",
  audio_stopped:
    "Audio stopped arriving from the microphone; your computer may have gone to sleep."
};

/** One line per gap for the recording widget's card. */
export const interruptionCardLines = (
  interruptions: RecordingInterruption[]
): string[] =>
  interruptions.map(({ fromMs, toMs, cause }) => {
    if (toMs === null) {
      return `Since ${formatClockTime(fromMs)}. ${CAUSE_SENTENCE[cause]} Recording is paused. Press the green microphone button to resume.`;
    }
    return `${upperFirst(formatGapRange(fromMs, toMs))} (${formatGapDuration(toMs - fromMs)}). ${CAUSE_SENTENCE[cause]}`;
  });

/**
 * The notice other Suno tabs show. Self-contained, so a snackbar left up after the facts change
 * is never false: it states the times rather than "now".
 */
export const interruptionSnackbarText = (
  interruptions: RecordingInterruption[]
): string | null => {
  if (interruptions.length === 0) return null;
  const open = interruptions.find(({ toMs }) => toMs === null);
  if (open) {
    return `Ambient Scribe is paused and part of this visit is missing from the transcript (since ${formatClockTime(open.fromMs)}). Go to the recording tab and press the green microphone button to resume.`;
  }
  const ranges = interruptions
    .map(({ fromMs, toMs }) => formatGapRange(fromMs, toMs ?? fromMs))
    .join(", ");
  return `Ambient Scribe: part of this visit is missing from the transcript (${ranges}).`;
};
